import * as React from 'react';
import { LocalizerHelper } from "../../helpers/localizerHelper";
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import MenuItemCard from './MenuItemCard';
import "./index.scss"
import { Section, Subsection } from '../../models/menu';

interface Props {
  onlyRead: boolean;
}

const Menu: React.FC<Props> = ({ onlyRead }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const topBarRef = React.useRef<HTMLDivElement>(null);
  const ignoreObserverRef = React.useRef(false);

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const menu = useSelector((state: RootState) => state.menu.menu);

  const scrollTargetIndexRef = React.useRef<number | null>(null);

  /* ----------------------------- */
  /* Intersection Observer logic   */
  /* ----------------------------- */
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !menu) return;

    const sections = container.querySelectorAll<HTMLElement>('.section-list-items');
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (!visible) return;

        const index = Number((visible.target as HTMLElement).dataset.index);
        if (isNaN(index)) return;

        // 🔒 If we're scrolling due to a click
        if (ignoreObserverRef.current) {
          // unlock ONLY when target is reached
          if (scrollTargetIndexRef.current === index) {
            ignoreObserverRef.current = false;
            scrollTargetIndexRef.current = null;
          } else {
            return; // ignore intermediate sections
          }
        }

        setSelectedIndex(index);
        centerTopBarItem(index);
      },
      {
        root: container,
        rootMargin: '-40% 0px -55% 0px',
      }
    );

    sections.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [menu]);

  /* ----------------------------- */
  /* Top bar helpers               */
  /* ----------------------------- */
  const centerTopBarItem = (index: number) => {
    const topBar = topBarRef.current;
    const item = document.getElementById(`top_bar_item_${index}`);
    if (!topBar || !item) return;

    const containerWidth = topBar.offsetWidth;
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;

    topBar.scrollTo({
      left: itemLeft - containerWidth / 2 + itemWidth / 2,
      behavior: 'smooth',
    });
  };

  const handleTopBarClick = (index: number) => {
    const section = document.getElementById(`section_menulist_${index}`);
    if (!section || !containerRef.current) return;

    ignoreObserverRef.current = true;
    scrollTargetIndexRef.current = index;

    setSelectedIndex(index);
    centerTopBarItem(index);

    containerRef.current.scrollTo({
      top: section.offsetTop,
      behavior: 'smooth',
    });
  };


  /* ----------------------------- */
  /* Render functions              */
  /* ----------------------------- */
  const renderTopBar = () => {
    const sections = menu?.layout?.filter(s => s.active) ?? [];

    return (
      <div className="topbar scrollmenu" ref={topBarRef}>
        {sections.map((section, index) => (
          <div
            key={index}
            id={`top_bar_item_${index}`}
            className={`top_bar_item scrollmenu-item ${index === selectedIndex ? 'selected' : ''}`}
            onClick={() => handleTopBarClick(index)}
          >
            {LocalizerHelper.localized(section, ['title'])}
          </div>
        ))}
      </div>
    );
  };

  const renderMenuList = () => {
    const sections = menu?.layout?.filter(s => s.active) ?? [];

    return (
      <div className="menu-main-container menulist" ref={containerRef}>
        {sections.map((section: Section, index: number) => (
          <div
            key={index}
            id={`section_menulist_${index}`}
            data-index={index}
            className="section-list-items"
          >
            {!!section.title && (
              <div className="section-title">
                {LocalizerHelper.localized(section, ['title'])}
              </div>
            )}

            {!!section.description && (
              <p className="section-description">
                {LocalizerHelper.localized(section, ['description'])}
              </p>
            )}

            {section.sub_sections.map((sub: Subsection, subIndex: number) => (
              <div key={subIndex} className="row subsection-row" style={{ marginTop: 22 }}>
                <div className="col-md-12">
                  {!!sub.title && (
                    <div className="section-submenu-title">
                      {LocalizerHelper.localized(sub, ['title'])}
                    </div>
                  )}

                  {!!sub.description && (
                    <p className="section-description">
                      {LocalizerHelper.localized(sub, ['description'])}
                    </p>
                  )}

                  {sub.menu_items.map((path: string, itemIndex: number) => (
                    <div key={itemIndex} className="menu-item-card">
                      <MenuItemCard menuItemPath={path} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="container container_topbar">
        <div className="row row-scroll">
          {renderTopBar()}
        </div>
      </div>
      {renderMenuList()}
    </>
  );
};

export default Menu;
